import { useLang } from "../lib/i18n";
import { legalCopy } from "../lib/i18n/legal";
import { ScreenShell } from "./screen-shell";
import { H1, MonoMeta, TopBar } from "./ui/primitives";

type Props = {
  onBack: () => void;
};

export function PrivacyScreen({ onBack }: Props) {
  const { lang } = useLang();
  const t = legalCopy[lang].privacy;

  return (
    <ScreenShell hideHeader>
      <div style={{ paddingTop: 6, paddingBottom: 18 }}>
        <TopBar back={t.topBarBack} title={t.topBarTitle} onBack={onBack} />
      </div>

      <article
        className="rounded-xl p-5 flex flex-col gap-4 leading-relaxed"
        style={{
          background: "var(--surface)",
          color: "var(--ink-2)",
          fontSize: 13,
          boxShadow: "0 0 0 1px var(--nav-border)",
        }}
      >
        <header>
          <H1 size={42} accent=".">
            {t.h1}
          </H1>
          <div style={{ marginTop: 6 }}>
            <MonoMeta>{t.metaUpdated}</MonoMeta>
          </div>
        </header>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s1Heading}</h3>
          <p>{t.s1Body}</p>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s2Heading}</h3>
          <ul className="list-disc pl-5 flex flex-col gap-1">
            <li>
              <strong>{t.s2ItemIdentifyingLead}</strong>
              {t.s2ItemIdentifyingRest}
            </li>
            <li>
              <strong>{t.s2ItemHealthLead}</strong>
              {t.s2ItemHealthRest}
            </li>
            <li>
              <strong>{t.s2ItemFoodLead}</strong>
              {t.s2ItemFoodRest}
            </li>
            <li>
              <strong>{t.s2ItemOpsLead}</strong>
              {t.s2ItemOpsRest}
            </li>
            <li>
              <strong>{t.s2ItemSessionLead}</strong>
              {t.s2ItemSessionBefore}
              <code>sf_session</code>
              {t.s2ItemSessionAfter}
            </li>
          </ul>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s3Heading}</h3>
          <ul className="list-disc pl-5 flex flex-col gap-1">
            <li>
              <strong>{t.s3ItemContractLead}</strong>
              {t.s3ItemContractRest}
            </li>
            <li>
              <strong>{t.s3ItemConsentLead}</strong>
              {t.s3ItemConsentRest}
            </li>
            <li>
              <strong>{t.s3ItemInterestLead}</strong>
              {t.s3ItemInterestRest}
            </li>
          </ul>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s4Heading}</h3>
          <ul className="list-disc pl-5 flex flex-col gap-1">
            <li>
              <strong>{t.s4ItemCloudflareLead}</strong>
              {t.s4ItemCloudflareRest}
            </li>
            <li>
              <strong>{t.s4ItemAnthropicLead}</strong>
              {t.s4ItemAnthropicBefore}
              <strong>{t.s4ItemAnthropicEmph}</strong>
              {t.s4ItemAnthropicAfter}
            </li>
          </ul>
          <p>
            <strong>{t.s4NoSellLead}</strong>
            {t.s4NoSellRest}
          </p>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s5Heading}</h3>
          <ul className="list-disc pl-5 flex flex-col gap-1">
            <li>{t.s5ItemAccount}</li>
            <li>{t.s5ItemAudit}</li>
            <li>{t.s5ItemRateLimit}</li>
            <li>{t.s5ItemPhotos}</li>
          </ul>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s6Heading}</h3>
          <ul className="list-disc pl-5 flex flex-col gap-1">
            <li>
              <strong>{t.s6ItemAccessLead}</strong>
              {t.s6ItemAccessRest}
            </li>
            <li>
              <strong>{t.s6ItemRectifyLead}</strong>
              {t.s6ItemRectifyRest}
            </li>
            <li>
              <strong>{t.s6ItemEraseLead}</strong>
              {t.s6ItemEraseRest}
            </li>
            <li>
              <strong>{t.s6ItemObjectLead}</strong>
              {t.s6ItemObjectRest}
            </li>
            <li>
              <strong>{t.s6ItemComplaintLead}</strong>
              {t.s6ItemComplaintRest}
            </li>
          </ul>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s7Heading}</h3>
          <p>
            {t.s7Before}
            <strong>{t.s7Emph}</strong>
            {t.s7Middle}
            <strong>{t.s7Phone}</strong>
            {t.s7After}
          </p>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s8Heading}</h3>
          <p>
            {t.s8Before}
            <code>sf_session</code>
            {t.s8After}
          </p>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s9Heading}</h3>
          <p>{t.s9Body}</p>
        </section>
      </article>
    </ScreenShell>
  );
}
